import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Button,
  alpha,
  useTheme,
  Collapse,
} from "@mui/material";
import {
  ChevronDown,
  ChevronUp,
  ArrowRightLeft,
  Mail,
  StickyNote,
  ListChecks,
  CheckCircle2,
  UserPlus,
  UserMinus,
  Plus,
} from "lucide-react";
import { fetchOrgActivity } from "../../../../../../../redux/actions/marketingAction";

const ACTION_CONFIG = {
  status_changed: { icon: ArrowRightLeft, color: "#8b5cf6", label: "Statut modifié" },
  email_sent: { icon: Mail, color: "#3b82f6", label: "Email envoyé" },
  note_updated: { icon: StickyNote, color: "#f59e0b", label: "Note modifiée" },
  task_created: { icon: ListChecks, color: "#6366f1", label: "Tâche créée" },
  task_completed: { icon: CheckCircle2, color: "#10b981", label: "Tâche complétée" },
  contact_linked: { icon: UserPlus, color: "#10b981", label: "Contact lié" },
  contact_unlinked: { icon: UserMinus, color: "#ef4444", label: "Contact délié" },
  created: { icon: Plus, color: "#6b7280", label: "Organisation créée" },
};

const formatRelativeTime = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin}m`;
  if (diffHours < 24) return `il y a ${diffHours}h`;
  if (diffDays < 7) return `il y a ${diffDays}j`;
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
};

const getActivityDescription = (log) => {
  const d = log.details || {};
  switch (log.action) {
    case "status_changed":
      return `${d.from || "?"} → ${d.to || "?"}`;
    case "email_sent":
      return d.subject ? `"${d.subject}" → ${(d.to || []).join(", ")}` : "Email envoyé";
    case "task_created":
      return d.title || "";
    case "task_completed":
      return d.title || "";
    case "contact_linked":
      return d.contact_name || d.contact_email || "";
    case "contact_unlinked":
      return d.contact_email || "";
    case "created":
      return d.name || "";
    default:
      return "";
  }
};

const FicheActivity = ({ orgId, activity }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(true);

  const items = activity?.items || [];
  const hasMore = (activity?.page || 1) < (activity?.total_pages || 1);

  const loadMore = () => {
    dispatch(fetchOrgActivity(orgId, (activity?.page || 1) + 1));
  };

  return (
    <Box>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2.5, py: 1, cursor: "pointer",
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.02) },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 1, color: theme.palette.text.secondary }}>
          Activité
        </Typography>
        {expanded ? <ChevronUp size={14} color={theme.palette.text.disabled} /> : <ChevronDown size={14} color={theme.palette.text.disabled} />}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 2.5, pb: 2 }}>
          {items.length === 0 ? (
            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, textAlign: "center", display: "block", py: 1 }}>
              Aucune activité
            </Typography>
          ) : (
            <Box sx={{ position: "relative", pl: 2.5 }}>
              {/* Timeline line */}
              <Box
                sx={{
                  position: "absolute", left: 5, top: 8, bottom: 8,
                  width: 1, bgcolor: theme.palette.divider,
                }}
              />

              {items.map((log, idx) => {
                const config = ACTION_CONFIG[log.action] || ACTION_CONFIG.created;
                const Icon = config.icon;
                const desc = getActivityDescription(log);

                return (
                  <Box
                    key={log.id}
                    sx={{
                      display: "flex", gap: 1.5, mb: 1.5,
                      position: "relative",
                    }}
                  >
                    {/* Dot */}
                    <Box
                      sx={{
                        position: "absolute", left: -16, top: 4,
                        width: 12, height: 12, borderRadius: "50%",
                        bgcolor: alpha(config.color, 0.15),
                        display: "flex", alignItems: "center", justifyContent: "center",
                        zIndex: 1,
                      }}
                    >
                      <Icon size={7} color={config.color} strokeWidth={2.5} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.7rem", color: theme.palette.text.primary }}>
                          {config.label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: "0.6rem", color: theme.palette.text.disabled, flexShrink: 0 }}>
                          {formatRelativeTime(log.created_at)}
                        </Typography>
                      </Box>
                      {desc && (
                        <Typography variant="caption" sx={{ fontSize: "0.65rem", color: theme.palette.text.secondary, display: "block" }} noWrap>
                          {desc}
                        </Typography>
                      )}
                      {log.performer_name && (
                        <Typography variant="caption" sx={{ fontSize: "0.6rem", color: theme.palette.text.disabled }}>
                          par {log.performer_name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {hasMore && (
            <Button
              size="small"
              onClick={loadMore}
              fullWidth
              sx={{ textTransform: "none", fontSize: "0.7rem", mt: 0.5, color: theme.palette.text.secondary }}
            >
              Voir plus...
            </Button>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default FicheActivity;
